export type TradeCalculatorSummary = {
  actualReceiveValue: number;
  actualSendValue: number;
  adjustedReceiveValue: number;
  adjustedSendValue: number;
  receiveAdjustment: number;
  sendAdjustment: number;
  rawAdjustmentReceiveValue: number;
  rawAdjustmentSendValue: number;
  equivalentPlayerValue: number;
  valueAdjustment: number;
  sideToEven: "receive" | "send" | null;
  addPlayerValue: number;
  favoredSide: "receive";
} | {
  actualReceiveValue: number;
  actualSendValue: number;
  adjustedReceiveValue: number;
  adjustedSendValue: number;
  receiveAdjustment: number;
  sendAdjustment: number;
  rawAdjustmentReceiveValue: number;
  rawAdjustmentSendValue: number;
  equivalentPlayerValue: number;
  valueAdjustment: number;
  sideToEven: "receive" | "send" | null;
  addPlayerValue: number;
  favoredSide: "send";
};

function sumAssetValues<T extends { value: number | null }>(assets: T[], getValue?: (asset: T) => number | null) {
  return assets.reduce((sum, asset) => sum + ((getValue ? getValue(asset) : asset.value) ?? 0), 0);
}

/*
WARNING: YOU ARE ABOUT TO MAKE A CHANGE THAT DIRECTLY AFFECTS THE FORMULA FOR YOUR TRADE CALCULATOR.

This file is the locked source of truth for dynasty trade value-adjustment math.

STOP.
Do not make any change in this file unless the user explicitly confirms they want to change the trade-calculator math.
Before making any edit here, you must tell the user exactly:
"WARNING: YOU ABOUT TO MAKE A CHANGE THAT DIRECTLY EFFECTS THE FORMULA FOR YOUR TRADE CALCULATOR"
Then wait for confirmation.

Do not change:
- the raw adjustment formula
- the inverse lookup helper
- the meaning of valueAdjustment
- the meaning of addPlayerValue
- the relationship between adjustedReceiveValue / adjustedSendValue and the two values above

Current locked meanings:
- valueAdjustment = displayed package/consolidation premium
- addPlayerValue = remaining value still needed to make the trade even after valueAdjustment is applied

If you touch anything below, you are changing the dynasty trade calculator itself, not just UI text.
Verify against source-of-truth examples before shipping any edit.
See project/docs/DYNASTY_TRADE_VALUE_LOGIC.md
*/
export function calculateKtcRawAdjustment(playerValue: number, tradeTopValue: number, overallMaxValue: number) {
  const p = Math.max(0, playerValue);
  const t = Math.max(1, tradeTopValue);
  const v = Math.max(1, overallMaxValue);
  if (!p) return 0;
  return p * (
    0.1 +
    0.04 * Math.pow(p / v, 8) +
    0.11 * Math.pow(p / t, 1.3) +
    0.22 * Math.pow(p / (v + 2000), 1.28)
  );
}

export function findKtcEquivalentValueForRawGap(targetRawGap: number, tradeTopValue: number, overallMaxValue: number) {
  if (targetRawGap <= 0) return 0;

  let low = 0;
  let high = Math.max(100, overallMaxValue * 2, tradeTopValue * 2);
  while (calculateKtcRawAdjustment(high, Math.max(tradeTopValue, high), overallMaxValue) < targetRawGap) {
    high *= 2;
    if (high > 1_000_000) break;
  }

  for (let i = 0; i < 60; i += 1) {
    const mid = (low + high) / 2;
    const midRaw = calculateKtcRawAdjustment(mid, Math.max(tradeTopValue, mid), overallMaxValue);
    if (midRaw < targetRawGap) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return Math.round(high);
}

type TradeSide = "receive" | "send";

const ONE_VS_X_VALUE_CUTOFFS = {
  top10: 7703,
  top15: 7581,
  top20: 6888,
  top22: 6842,
  top29: 6203,
  top30: 6168,
  top32: 6137,
  top35: 6026,
  rank45OrBetter: 5545,
} as const;

function getAssetValue<T extends { value: number | null }>(asset: T, getValue?: (asset: T) => number | null) {
  return Math.max(0, getValue ? (getValue(asset) ?? 0) : (asset.value ?? 0));
}

function getSortedSideValues<T extends { value: number | null }>(assets: T[], getValue?: (asset: T) => number | null) {
  return assets
    .map((asset) => getAssetValue(asset, getValue))
    .sort((a, b) => b - a);
}

function getOneVsXPremium<T extends { value: number | null }>(
  receiveAssets: T[],
  sendAssets: T[],
  getValue?: (asset: T) => number | null
) {
  if (receiveAssets.length === 1 && sendAssets.length === 1) {
    return {
      handled: true,
      premiumSide: null,
      valueAdjustment: 0,
    };
  }

  const sides = [
    { side: "receive" as const, assets: receiveAssets, values: getSortedSideValues(receiveAssets, getValue) },
    { side: "send" as const, assets: sendAssets, values: getSortedSideValues(sendAssets, getValue) },
  ];
  const singleSide = sides.find((entry) => entry.assets.length === 1);
  const packageSide = sides.find((entry) => entry.assets.length === 2 || entry.assets.length === 3);

  if (!singleSide || !packageSide) return null;
  if (singleSide.assets.length !== 1) return null;
  if (![2, 3].includes(packageSide.assets.length)) return null;

  const singleTopValue = singleSide.values[0] ?? 0;
  const packageTopValue = packageSide.values[0] ?? 0;
  const packageSecond = packageSide.values[1] ?? 0;
  const packageThird = packageSide.values[2] ?? 0;
  const packageTotal = packageSide.values.reduce((sum, value) => sum + value, 0);
  const gap = Math.abs(singleTopValue - packageTopValue);
  const singleSideStrongest = singleTopValue >= packageTopValue;

  let premiumSide: TradeSide | null = null;

  if (packageSide.assets.length === 2) {
    if (singleSideStrongest) {
      if (gap <= 235) {
        premiumSide = null;
      } else if (
        singleTopValue >= ONE_VS_X_VALUE_CUTOFFS.top15 &&
        packageTopValue >= ONE_VS_X_VALUE_CUTOFFS.top20
      ) {
        premiumSide = singleSide.side;
      } else if (packageSecond >= 5067 && gap <= 658) {
        premiumSide = null;
      } else if (packageSecond >= 3691 && singleTopValue <= ONE_VS_X_VALUE_CUTOFFS.top35 && gap <= 430) {
        premiumSide = null;
      } else if (packageSecond >= 3691 && packageTopValue <= ONE_VS_X_VALUE_CUTOFFS.top35 && gap <= 280) {
        premiumSide = null;
      } else if (packageSecond >= 3523 && packageTopValue <= ONE_VS_X_VALUE_CUTOFFS.top32 && gap <= 260) {
        premiumSide = null;
      } else if (packageSecond >= 3950 && gap <= 350) {
        premiumSide = null;
      } else {
        premiumSide = singleSide.side;
      }
    } else {
      if (
        singleTopValue >= ONE_VS_X_VALUE_CUTOFFS.top10 &&
        packageTopValue >= ONE_VS_X_VALUE_CUTOFFS.top10
      ) {
        premiumSide = null;
      } else if (packageSecond >= 3691) {
        premiumSide = null;
      } else if (singleTopValue <= ONE_VS_X_VALUE_CUTOFFS.top30 && packageSecond >= 3350) {
        premiumSide = null;
      } else if (packageTopValue <= ONE_VS_X_VALUE_CUTOFFS.rank45OrBetter && packageSecond >= 3350) {
        premiumSide = null;
      } else if (gap < 296) {
        premiumSide = null;
      } else if (gap <= 318.5) {
        premiumSide = packageSecond <= 3064 ? packageSide.side : null;
      } else if (packageSecond <= 3086) {
        premiumSide = packageTotal <= 11125 ? packageSide.side : null;
      } else if (packageSecond <= 3350) {
        premiumSide = packageTotal <= 9114 ? packageSide.side : null;
      } else if (gap <= 355) {
        premiumSide = null;
      } else if (gap <= 460) {
        premiumSide = packageTotal <= 9639.5 ? packageSide.side : null;
      } else if (packageTopValue >= ONE_VS_X_VALUE_CUTOFFS.top32 && packageSecond <= 3682) {
        premiumSide = packageSide.side;
      } else {
        premiumSide = null;
      }
    }
  } else {
    if (singleSideStrongest) {
      premiumSide = singleSide.side;
    } else if (
      singleTopValue >= ONE_VS_X_VALUE_CUTOFFS.top10 &&
      packageTopValue >= ONE_VS_X_VALUE_CUTOFFS.top10
    ) {
      premiumSide = null;
    } else if (packageThird <= 2277) {
      premiumSide = null;
    } else if (singleTopValue >= ONE_VS_X_VALUE_CUTOFFS.top22) {
      premiumSide = null;
    } else {
      premiumSide = singleSide.side;
    }
  }

  if (!premiumSide) {
    return {
      handled: true,
      premiumSide: null,
      valueAdjustment: 0,
    };
  }

  let valueAdjustment = 0;
  if (packageSide.assets.length === 2 && premiumSide === singleSide.side) {
    valueAdjustment = 317.2531 + 1.5596 * gap + 0.1833 * packageTotal - 0.5282 * packageSecond;
  } else if (packageSide.assets.length === 2 && premiumSide === packageSide.side) {
    valueAdjustment = 39.8094 + 0.5927 * gap + 0.1291 * packageTotal - 0.2843 * packageSecond;
  } else if (packageSide.assets.length === 3 && premiumSide === singleSide.side) {
    valueAdjustment = 716.5213 + 0.9855 * gap + 0.0261 * packageTotal;
  }

  return {
    handled: true,
    premiumSide,
    valueAdjustment: Math.round(Math.max(0, valueAdjustment)),
  };
}

export function calculateKtcStyleAdjustedTotals<T extends { value: number | null }>(
  receiveAssets: T[],
  sendAssets: T[],
  getValue?: (asset: T) => number | null,
  overallMaxValue?: number
): TradeCalculatorSummary {
  const receiveRaw = sumAssetValues(receiveAssets, getValue);
  const sendRaw = sumAssetValues(sendAssets, getValue);

  if (!receiveRaw && !sendRaw) {
    return {
      actualReceiveValue: 0,
      actualSendValue: 0,
      adjustedReceiveValue: 0,
      adjustedSendValue: 0,
      receiveAdjustment: 0,
      sendAdjustment: 0,
      rawAdjustmentReceiveValue: 0,
      rawAdjustmentSendValue: 0,
      equivalentPlayerValue: 0,
      valueAdjustment: 0,
      sideToEven: null,
      addPlayerValue: 0,
      favoredSide: "receive",
    };
  }

  const actualReceiveValue = Math.round(receiveRaw);
  const actualSendValue = Math.round(sendRaw);

  const tradeTopValue = Math.max(
    1,
    ...[...receiveAssets, ...sendAssets].map((asset) => Math.max(0, getValue ? (getValue(asset) ?? 0) : (asset.value ?? 0)))
  );
  const maxOverallValue = Math.max(tradeTopValue, overallMaxValue ?? 0, 1);
  const rawAdjustmentReceiveValue = receiveAssets.reduce((sum, asset) => {
    const playerValue = Math.max(0, getValue ? (getValue(asset) ?? 0) : (asset.value ?? 0));
    return sum + calculateKtcRawAdjustment(playerValue, tradeTopValue, maxOverallValue);
  }, 0);
  const rawAdjustmentSendValue = sendAssets.reduce((sum, asset) => {
    const playerValue = Math.max(0, getValue ? (getValue(asset) ?? 0) : (asset.value ?? 0));
    return sum + calculateKtcRawAdjustment(playerValue, tradeTopValue, maxOverallValue);
  }, 0);
  const favoredSide = rawAdjustmentReceiveValue >= rawAdjustmentSendValue ? "receive" as const : "send" as const;
  const rawGap = Math.abs(rawAdjustmentReceiveValue - rawAdjustmentSendValue);
  const equivalentPlayerValue = findKtcEquivalentValueForRawGap(rawGap, tradeTopValue, maxOverallValue);
  const oneVsXPremium = getOneVsXPremium(receiveAssets, sendAssets, getValue);
  const valueAdjustment = oneVsXPremium?.handled ? oneVsXPremium.valueAdjustment : equivalentPlayerValue;
  const adjustedFavoredSide = oneVsXPremium?.handled
    ? (oneVsXPremium.premiumSide ?? favoredSide)
    : favoredSide;
  const receiveAdjustment = adjustedFavoredSide === "receive" ? valueAdjustment : 0;
  const sendAdjustment = adjustedFavoredSide === "send" ? valueAdjustment : 0;
  const adjustedReceiveValue = actualReceiveValue + receiveAdjustment;
  const adjustedSendValue = actualSendValue + sendAdjustment;
  const sideToEven = valueAdjustment > 0
    ? (adjustedFavoredSide === "receive" ? "send" as const : "receive" as const)
    : null;
  const addPlayerValue = sideToEven === "receive"
    ? Math.max(0, adjustedSendValue - actualReceiveValue)
    : Math.max(0, adjustedReceiveValue - actualSendValue);

  return {
    actualReceiveValue,
    actualSendValue,
    adjustedReceiveValue,
    adjustedSendValue,
    receiveAdjustment,
    sendAdjustment,
    rawAdjustmentReceiveValue,
    rawAdjustmentSendValue,
    equivalentPlayerValue,
    valueAdjustment,
    sideToEven,
    addPlayerValue,
    favoredSide: adjustedFavoredSide,
  };
}
